/**
 * FIX2_FE_numeric_branch_comparison_spec.md — shared helpers for BRANCH edge conditions.
 *
 * PR #669 review (C1) corrected the wire shape: a numeric comparison is GraphEdge's own
 * `condition_op: BranchCondition | null` field (matching the orchestrator's `BranchCondition`
 * schema exactly — {op, field, value}), never an object embedded in `condition`, which stays a
 * plain legacy string (or null) only.
 */
import type { BranchCondition, EdgeComparisonOperator } from './workflowGraphTypes';

export type { BranchCondition, EdgeComparisonOperator };

export const EDGE_OPERATOR_OPTIONS: { code: EdgeComparisonOperator; label: string }[] = [
  { code: '==', label: 'Equals' },
  { code: '!=', label: 'Not equal to' },
  { code: '>', label: 'Greater than' },
  { code: '<', label: 'Less than' },
  { code: '>=', label: 'Greater than or equal to' },
  { code: '<=', label: 'Less than or equal to' },
];

const OPERATOR_CODES = new Set(EDGE_OPERATOR_OPTIONS.map((o) => o.code));

/**
 * PR #669 review (C2) — the orchestrator's validator explicitly rejects any edge carrying
 * `condition_op` today ("condition_op is not supported by the workflow runtime yet"); the
 * workflow-service interpreter that would actually execute it doesn't exist yet. Gates the
 * "Add a numeric comparison instead" entry point for NEW edges so nobody can build a condition
 * guaranteed to fail backend validation on save/run-test. Flip to true once that interpreter
 * ships — no other code change needed. An edge that already carries a valid condition_op (e.g.
 * from an AI draft, or once this flips back off again after being on) is still shown/editable in
 * the structured view regardless of this flag — it's only the creation path that's gated.
 */
export const NUMERIC_BRANCH_CONDITIONS_ENABLED = false;

export function isValidBranchCondition(value: unknown): value is BranchCondition {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as Record<string, unknown>).op === 'string' &&
    OPERATOR_CODES.has((value as Record<string, unknown>).op as EdgeComparisonOperator) &&
    typeof (value as Record<string, unknown>).field === 'string' &&
    (value as Record<string, unknown>).field !== '' &&
    typeof (value as Record<string, unknown>).value === 'number'
  );
}

/** Human-readable canvas label, e.g. "days_requested < 2" — falls back to "< 2" if field is
 * somehow blank. Legacy plain-string conditions pass through unchanged. Used both on load
 * (docToRfEdges) and on every edit (handleUpdateEdgeCondition) so the diagram always shows
 * something readable, never raw data. */
export function formatEdgeConditionLabel(
  condition: string | null | undefined,
  conditionOp: BranchCondition | null | undefined
): string | undefined {
  if (conditionOp && isValidBranchCondition(conditionOp)) {
    return conditionOp.field ? `${conditionOp.field} ${conditionOp.op} ${conditionOp.value}` : `${conditionOp.op} ${conditionOp.value}`;
  }
  return condition ?? undefined;
}
